import axios, { AxiosRequestConfig } from 'axios';
import {
	GetSplitResponse,
	GetTransactionsParameters,
	SplitDetailsResponse,
	GetSplitDetailsPayload,
	GetKycHistoryPayload,
} from '@shared/api/transactions';
import {
	GetBalanceMutationsParameters,
	GetBalanceMutationsResponse,
} from '@shared/api/balanceMutations';
import { GetPayoutsParameters, GetXmlFileResponse } from '@shared/api/payouts';
import { GetMerchantPayoutsParameters, MerchantPayout } from '@shared/api/merchantPayouts';
import {
	PspRawDataPayload,
	PspRawDataResponse,
	PspReportQueryParameters,
} from '@shared/api/pspReports';
import { AllocateStatementParameters, BankReportQueryParameters } from '@shared/api/bankReports';
import {
	GenerateReportParameters,
	GetReportsParameters,
	GetReportsResponse,
} from '@shared/api/reports';
import { GetAccountsResponse, GetExternalAccountResponse } from '@shared/api/accounts';
import { PayoutConfig } from '@shared/api/payoutConfig';
import {
	KycHistoryItem,
	SellerDetailsItem,
	SellerPendingKyc,
	SellerResponse,
} from '@shared/types/sellers';
import {
	deleteAccountHolderPayload,
	SellerDetailsRequestPayload,
	SellersRequestPayload,
} from '@shared/api/sellers';
import { DivisionDetailsItem, DivisionDetailsRequestPayload } from '@shared/types/division';
import { Division } from '@shared/types/divisionInformation';
import { Pagination } from '@shared/types/generic';
import { REPORT_TYPES } from '@shared/views/reports/reportsData';
import { PspConfiguration } from '@shared/types/pspConfiguration';
import { HostedOnboardingConfiguration } from './configurations';
import {
	CreatePayoutRequestPayload,
	PayoutRequest,
	PayoutRequestPayload,
	PayoutRequestResponse,
	PayoutRequestResponseData,
	DeletePayoutRequestPayload,
	ProcessDivisionPayoutRequestParams,
	RegenerateBouncebackPayload,
	PayoutRequestDetailsPayload,
	TriggerScheduledPayoutRequest,
	TriggerScheduledResponse,
} from './payoutRequests';
import {
	GetMerchantsQueries,
	GetMerchantsResponse,
	GetMerchantsWithPayoutRequestsQueries,
	GetMerchantsWithPayoutRequestsResponse,
	Merchant,
} from './merchants';
import {
	GetCurrenciesPayload,
	GetCurrenciesResponse,
	PostBalanceTransferPayload,
	PostBalanceTransferResponse,
} from './balanceTransfer';

type ApiResponse = {
	status: number;
	data: {
		items: Array<unknown>;
		pagination: {
			page: number;
			limit: number;
			total: number;
		};
	};
};
export interface QueryParameters {
	page?: number;
	limit?: number;
	sort?: string;
	direction?: string;
	account?: string;
	from?: string;
	to?: string;
	status?: string;
}
const IS_OPS = process.env.VUE_APP_TYPE === 'ops';

/**
 * @function
 * @name getAccounts
 * Retrieves all the accounts of a merchant
 *
 * (no merchant or division information needed)
 * url MD: 'v2/reports/accounts'
 * url OPS: 'invoicing/v2/merchants/${merchantCode}/divisions/${divisionCode}}/reports/accounts'
 *
 * @returns {Promise<{ data: GetAccountsResponse }>} A promise with lists of accounts grouped by type
 */
export const getAccounts = async (
	merchantCode?: string,
	divisionCode?: string
): Promise<{ data: GetAccountsResponse }> => {
	const url = IS_OPS
		? `invoicing/merchants/${merchantCode}/divisions/${divisionCode}/reports/accounts`
		: 'v2/reports/accounts';
	return axios.get(url);
};

/**
 * @function
 * @name postAllocateBankStatement
 * Allocates the bank statement to a virtual account. Can be a division or a seller.
 *
 * @param {AllocateStatementParameters} params - Required date and account information to generate the report
 *
 * url (no merchant or division information needed): 'v2/fund/bank-line/allocate'
 *
 * @returns {Promise<{data: boolean}>} A promise with HTTP data and no body.
 */
export const postAllocateBankStatement = async (
	params: AllocateStatementParameters
): Promise<{ data: boolean }> => {
	const url = `main/api/v1/fund/bank-line/allocate`;
	return axios.post(url, params);
};

/**
 * @function
 * @name postApprovePayout
 * Approves a pending payout
 *
 * @param {string} merchantCode - The code of the merchant.
 * @param {string} divisionCode - The code of the division.
 * @param {string} payoutCode - The code of the payout.
 * @param {string} approverEmail - The email of the user that's approving the payout.
 */
export const postApprovePayout = async (
	merchantCode: string,
	divisionCode: string,
	payoutCode: string,
	approverEmail: string
): Promise<{ data: MerchantPayout }> => {
	const headers: AxiosRequestConfig['headers'] = {
		'Merchant-Code': merchantCode,
		'Merchant-Division-Code': divisionCode,
	};

	return axios.post(
		`main/api/v1/fund/payouts/merchants/${merchantCode}/divisions/${divisionCode}/${payoutCode}/approve/${approverEmail}`,
		{
			headers,
		}
	);
};

/**
 * @function
 * @name getAvailablePaymentProviders
 * Retrieves all the available payment providers we have for configuration
 *
 * url OPS: 'psp-adapter/api/v1/psp/available-integrations'
 * url MD: 'v1/psp/available-integrations'
 *
 * @returns {Promise<ApiResponse>} A promise with lists of accounts grouped by type
 */
export const getAvailablePaymentProviders = async (): Promise<ApiResponse> => {
	const url = IS_OPS
		? `psp-adapter/api/v1/psp/available-integrations`
		: `v1/psp/available-integrations`;
	return axios.get(url);
};

/**
 * @function
 * @name postPaymentProvider
 * adds a new payment provider configuration
 *
 * url OPS: 'psp-adapter/api/v1/merchant/${merchantCode}/division/${divisionCode}/psp/integration'
 * url MD: 'v1/psp/integration'
 *
 * @returns {Promise<PspConfiguration>} A promise with lists of accounts grouped by type
 */
export const postPaymentProvider = async (
	psp: PspConfiguration,
	merchantCode: string,
	divisionCode: string
): Promise<PspConfiguration> => {
	const url = IS_OPS
		? `psp-adapter/api/v1/merchants/${merchantCode}/divisions/${divisionCode}/psp/integration`
		: `/v1/psp/integration`;
	return axios.post(url, psp);
};

/**
 * @function
 * @name patchPaymentProvider
 *
 * updates an existing payment provider configuration
 *
 * url OPS: `psp-adapter/api/v1/merchants/${merchantCode}/divisions/${divisionCode}/psp/integration/${psp.id}`
 * url MD: `/v1/psp/integration/${psp.id}`
 *
 * @returns {Promise<PspConfiguration>} A promise with lists of accounts grouped by type
 */
export const patchPaymentProvider = async (
	psp: PspConfiguration,
	merchantCode: string,
	divisionCode: string
): Promise<PspConfiguration> => {
	const url = IS_OPS
		? `psp-adapter/api/v1/merchants/${merchantCode}/divisions/${divisionCode}/psp/integration/${psp.id}`
		: `/v1/psp/integration/${psp.id}`;
	return axios.patch(url, psp);
};

/**
 * @function
 * @name deletePaymentProvider
 * deletes a specified payment provider configuration
 *
 * url OPS: 'psp-adapter/api/v1/merchant/${merchantCode}/division/${divisionCode}/psp/integration/${psp.id}'
 * url MD: 'v1/psp/integration/${psp.id}'
 *
 * @returns {Promise<ApiResponse>} A promise with lists of accounts grouped by type
 */
export const deletePaymentProvider = async (
	pspId: string,
	merchantCode: string,
	divisionCode: string
): Promise<ApiResponse> => {
	const url = IS_OPS
		? `psp-adapter/api/v1/merchants/${merchantCode}/divisions/${divisionCode}/psp/integration/${pspId}`
		: `/v1/psp/integration/${pspId}`;
	return axios.delete(url);
};

/**
 * @function
 * @name getDivisionPaymentProviders
 * Retrieves all the available payment providers we have configured for a merchant/division
 *
 * url OPS: 'psp-adapter/api/v1/merchant/${merchantCode}/division/${divisionCode}/psp/integrations'
 * url MD: 'v1/psp/integrations'
 *
 * @returns {Promise<ApiResponse>} A promise with lists of accounts grouped by type
 */
export const getDivisionPaymentProviders = async (
	merchantCode: string,
	divisionCode: string
): Promise<ApiResponse> => {
	const url = IS_OPS
		? `psp-adapter/api/v1/merchants/${merchantCode}/divisions/${divisionCode}/psp/integration`
		: `/v1/psp/integration`;
	return axios.get(url);
};

/**
 * @function
 * @name getBalanceMutations
 * Allocates the bank statement to a virtual account. Can be a division or a seller.
 *
 * @param {string} merchantCode - Required for the url of the request.
 * @param {string} divisionCode - Required for the url of the request.
 * @param {string} virtualAccountCode - Required for the url of the request.
 * @param {GetBalanceMutationsParameters} params - Required for the url of the request.
 *
 * url OPS: `insights/api/v1/merchants/${merchantCode}/divisions/${divisionCode}/virtualAccounts/${virtualAccountCode}/balance-history`
 * url MD: `/v1/virtualAccounts/${virtualAccountCode}/balance-history`
 *
 * @returns {Promise<{data: GetBalanceMutationsResponse}>} A promise with HTTP data and no body.
 */
export const getBalanceMutations = async (
	merchantCode: string,
	divisionCode: string,
	virtualAccountCode: string,
	params: GetBalanceMutationsParameters
): Promise<{ data: GetBalanceMutationsResponse }> => {
	const url = IS_OPS
		? `insights/api/v1/merchants/${merchantCode}/divisions/${divisionCode}/virtualAccounts/${virtualAccountCode}/balance-history`
		: `/v1/virtualAccounts/${virtualAccountCode}/balance-history`;

	return axios.get(url, { params });
};

/**
 * @function
 * @name postBalanceTransfer
 * Balance transfer between virtual accounts
 *
 * url OPS: '/main/api/v2/fund/internal/balance/transfer'
 * url MD: '/v2/fund/balance/transfer'
 *
 * @param {object} payload
 *
 * @returns {Promise<{ data: PostBalanceTransferResponse }>}
 */
export const postBalanceTransfer = async (
	payload: PostBalanceTransferPayload
): Promise<{ data: PostBalanceTransferResponse }> => {
	const url = IS_OPS
		? '/main/api/v2/fund/internal/balance/transfer'
		: '/v2/fund/balance/transfer';

	const options = IS_OPS
		? {
				headers: {
					'Merchant-Code': process.env.VUE_APP_PAYAUT_MERCHANT_CODE,
					'Merchant-Division-Code': process.env.VUE_APP_PAYAUT_DIVISION_CODE,
				},
		  }
		: {};

	return axios.post(url, payload, options);
};

/**
 * @function
 * @name getDivision
 * Get detailed info about a seller using fragments
 *
 * @param {string} merchantCode - Required for the url of the request.
 * @param {string} divisionCode - Required for the url of the request.
 *
 * url OPS: 'accountowner/api/v2/accountowners/merchants/{merchantCode}/divisions/{divisionCode}/detailed'
 * url MD: 'v2/divisions/detailed'
 *
 * @return {Promise<{ data: DivisionDetailsItem }>}
 */
export const getDivision = async ({
	merchantCode,
	divisionCode,
}: DivisionDetailsRequestPayload): Promise<{ data: DivisionDetailsItem }> => {
	const url = IS_OPS // eslint-disable-next-line max-len
		? `accountowner/api/v2/accountowners/merchants/${merchantCode}/divisions/${divisionCode}/detailed`
		: `v2/divisions/detailed`;

	return axios.get(url);
};

/**
 * @function
 * @name getDivisionInformation
 *
 * OPS url: `accountowner/api/v2/accountowners/merchants/${merchantCode}/divisions/detailed`
 * MD url: '/v2/merchants/divisionInfos'
 *
 * @returns { Promise<{ data: Division[] }> }
 */
export const getDivisionInformation = async (
	merchantCode: string
): Promise<{ data: Division[] }> => {
	const url = IS_OPS
		? `accountowner/api/v2/accountowners/merchants/${merchantCode}/divisions/detailed`
		: '/v2/merchants/divisionInfos';
	return axios.get(url);
};

/**
 * @function
 * @name getHostedOnboardingConfig
 *
 * @param {string} merchantCode - Required for the url of the request.
 * @param {string} divisionCode - Required for the url of the request.
 *
 * MD url: 'onboarding-api/api/v1/whitelabel/merchant/{merchantCode}/division/{divisionCode}/config'
 * MD url: '/v1/onboarding/whitelabel/config'
 *
 * @returns { Promise<ApiResponse> }
 */
export const getHostedOnboardingConfig = async (
	merchantCode: string,
	divisionCode: string
): Promise<ApiResponse> => {
	const url = IS_OPS // eslint-disable-next-line max-len
		? `onboarding-api/api/v1/whitelabel/merchant/${merchantCode}/division/${divisionCode}/config`
		: '/v1/onboarding/whitelabel/config';

	return axios.get(url);
};

/**
 * @function
 * @name putHostedOnboardingConfig
 *
 * @param {string} merchantCode - Required for the url of the request.
 * @param {string} divisionCode - Required for the url of the request.
 *
 * MD url: 'onboarding-api/api/v1/whitelabel/merchant/{merchantCode}/division/{divisionCode}/config'
 * MD url: '/v1/onboarding/whitelabel/config'
 *
 * @returns { Promise<ApiResponse> }
 */
export const putHostedOnboardingConfig = async (
	merchantCode: string,
	divisionCode: string,
	payload: HostedOnboardingConfiguration
): Promise<ApiResponse> => {
	const url = IS_OPS // eslint-disable-next-line max-len
		? `onboarding-api/api/v1/whitelabel/merchant/${merchantCode}/division/${divisionCode}/config`
		: '/v1/onboarding/whitelabel/config';

	return axios.put(url, payload);
};

/**
 * @function
 * @name uploadHostedOnboardingLogo
 *
 * @param {string} merchantCode - Required for the url of the request.
 * @param {string} divisionCode - Required for the url of the request.
 *
 * MD url: 'onboarding-api/api/v1/whitelabel/merchant/{merchantCode}/division/{divisionCode}/config/logo'
 * MD url: '/v1/onboarding/whitelabel/config'
 *
 * @returns { Promise<HostedOnboardingConfiguration> }
 */
export const uploadHostedOnboardingLogo = async (
	merchantCode: string,
	divisionCode: string,
	payload: FormData
): Promise<HostedOnboardingConfiguration> => {
	const url = IS_OPS // eslint-disable-next-line max-len
		? `onboarding-api/api/v1/whitelabel/merchant/${merchantCode}/division/${divisionCode}/config/logo`
		: '/v1/onboarding/whitelabel/config/logo';

	return axios.post(url, payload);
};

/**
 * @function
 * @name getSellerExternalAccounts
 *
 * OPS url: 'accountowner/api/v2/accountowners/merchants/{merchantCode}/divisions/{divisionCode}/accountholders/{sellerCode}/externalAccounts'
 *
 * @returns { Promise<{ data: GetExternalAccountResponse }> }
 */
export const getSellerExternalAccounts = async (
	merchantCode: string,
	divisionCode: string,
	sellerCode: string
): Promise<{ data: GetExternalAccountResponse }> => {
	return axios.get(
		`accountowner/api/v2/accountowners/merchants/${merchantCode}/divisions/${divisionCode}/accountholders/${sellerCode}/externalAccounts`
	);
};

/**
 * @function
 * @name getDivisionExternalAccounts
 *
 * OPS url: '/api/v2/accountowners/merchants/{merchantCode}/divisions/{divisionCode}/externalAccounts'
 *
 * @returns { Promise<{ data: GetExternalAccountResponse }> }
 */
export const getDivisionExternalAccounts = async (
	merchantCode: string,
	divisionCode: string
): Promise<{ data: GetExternalAccountResponse }> => {
	return axios.get(
		`/api/v2/accountowners/merchants/${merchantCode}/divisions/${divisionCode}/externalAccounts`
	);
};

export const getMerchantPayouts = async (
	merchantCode: string,
	divisionCode: string,
	params: GetMerchantPayoutsParameters
) => {
	let merchantPayoutURL = 'main/api/v1/fund/payouts';

	if (merchantCode && divisionCode) {
		merchantPayoutURL += `/merchants/${merchantCode}/divisions/${divisionCode}`;
	}
	return axios.get(merchantPayoutURL, { params });
};

const sanitizeParameters = (params: GetPayoutsParameters) => {
	const toRemove = ['sellerCode', 'divisionCode', 'merchantCode'];

	const sanitizedParams = { ...params };
	toRemove.forEach((key) => {
		const extraKey = key as keyof GetPayoutsParameters;
		if (sanitizedParams[extraKey]) {
			delete sanitizedParams[extraKey];
		}
	});

	return sanitizedParams;
};

export const getPayouts = async (
	merchantCode: string,
	divisionCode: string,
	params: GetPayoutsParameters
) => {
	const cleanedParams = sanitizeParameters(params);
	if (IS_OPS) {
		return axios.get(
			`insights/api/v2/payouts/merchants/${merchantCode}/divisions/${divisionCode}`,
			{ params: cleanedParams }
		);
	}
	return axios.get('/v2/payouts', { params: cleanedParams });
};

/**
 * Requests the list of PSPs.
 *
 * @returns {Promise<{data: Array<string>}>} A promise that resolves into an array of strings.
 *
 * We make this call to avoid hard coding the list of PSPs here,
 * so when we implement a new PSP, we don't have to change the code here.
 */
export const getPaymentProviders = async (): Promise<{ data: Array<string> }> => {
	const url = IS_OPS ? 'main/api/v1/reconciliation/acquirers' : '/v2/acquirers';
	return axios.get(url);
};

/**
 * Requests BANK reports.
 *
 * @param {string} merchantCode - The code of the merchant.
 * @param {string} divisionCode - The code of the division.
 * @param {BankReportQueryParameters} params - The parameters for the report query.
 * @returns {Promise<unknown>} A promise that resolves to the response of the report request.
 *
 * This is only for ops and can be used to get all Bank reports or filtered by selected merchant
 * url for all reports: '/api/v1/reconciliation/bank-statements-info'
 * url for filtered reports:
 * '/api/v1/reconciliation/merchants/{merchantCode}/divisions/{divisionCode}/bank-statements-info'
 */
export const getBankReports = async (
	merchantCode: string,
	divisionCode: string,
	params: BankReportQueryParameters
) => {
	let url = `main/api/v1/reconciliation/`;
	if (merchantCode && divisionCode) {
		url += `merchants/${merchantCode}/divisions/${divisionCode}/bank-statements-info`;
	} else {
		url += 'bank-statements-info';
	}
	return axios.get(url, { params });
};

/**
 * Requests PSP reports.
 *
 * @param {string} merchantCode - The code of the merchant.
 * @param {string} divisionCode - The code of the division.
 * @param {PspReportQueryParameters} params - The parameters for the report query.
 * @returns {Promise<unknown>} A promise that resolves to the response of the report request.
 *
 * This is only for ops and can be used to get all psp reports or filtered by selected merchant
 * url: 'insights/api/v1/reconciliation/psp-reports/search'
 */
export const postPspReports = async (
	merchantCode: string,
	divisionCode: string,
	params: PspReportQueryParameters
) => {
	const url = IS_OPS
		? 'insights/api/v1/reconciliation/psp-reports/search'
		: 'v1/reconciliation/psp-reports/search';

	if (IS_OPS) {
		const headers: AxiosRequestConfig['headers'] = IS_OPS
			? {
					'Merchant-Code': merchantCode,
					'Merchant-Division-Code': divisionCode,
			  }
			: {};

		return axios.post(url, {
			filter: params.filter,
			pagination: params.pagination,
			headers,
		});
	}
	return axios.post(url, {
		filter: params.filter,
		pagination: params.pagination,
	});
};

export const getTransactions = async (
	merchantCode: string,
	divisionCode: string,
	params: GetTransactionsParameters
) => {
	const cleanedParams = sanitizeParameters(params);
	if (IS_OPS) {
		return axios.get(
			`insights/api/v2/transactions/merchants/${merchantCode}/divisions/${divisionCode}`,
			{ params: cleanedParams }
		);
	}
	return axios.get('/v2/transactions', { params: cleanedParams });
};

/**
 * @function
 * @name getPayoutRequests
 * Retrieves payout requests from the server.
 *
 * @param {Object} payload
 * @param {string} payload.merchantCode -- null if no merchant is selected
 * @param {string} payload.divisionCode -- null if no merchant is selected
 *
 * Payout requests can be fetched for specific or all merchants
 * url for all merchants: 'main/api/v1/fund/payout-requests'
 * url for specific merchant: 'main/api/v1/fund/merchants/{merchantCode}/divisions/{divisionCode}'
 *
 * url for merchant dashboard: '/v1/fund/payout-requests'
 *
 * @returns {Promise<Object>} A promise that resolves to an object containing the payout request data.
 *
 */
export const getPayoutRequests = async ({
	params,
	merchantCode,
	divisionCode,
}: PayoutRequestPayload): Promise<{ data: PayoutRequestResponseData }> => {
	// TODO: restore 1 based when pagination is fixed in the server
	// the page offsets + 1 and - 1 are needed right now because the sellers endpoint is 0 based in the server.
	// This should be fixed in the server some day. Then we remove the offsets here as well.
	const offsetParams = {
		...params,
		// eslint-disable-next-line no-underscore-dangle
		_pageNumber: params._pageNumber && params._pageNumber > 0 ? params._pageNumber - 1 : 0,
	};
	let url = IS_OPS ? 'main/api/v1/fund/payout-requests' : '/v1/fund/payout-requests';
	if (merchantCode && divisionCode) url += `/merchants/${merchantCode}/divisions/${divisionCode}`;

	const { data } = await axios.get(url, { params: offsetParams });

	data.pageNumber += 1;

	return { data };
};

/**
 * @function
 * @name deletePayoutRequests
 * Retrieves payout requests from the server.
 *
 * @param {Object} payload
 * @param {string} payload.merchantCode -- null if no merchant is selected
 * @param {string} payload.divisionCode -- null if no merchant is selected
 * @param {string} payload.payoutRequestCode -- the code of the payout request to be deleted
 *
 * Delete a specific payout request by code
 *
 */
export const deletePayoutRequest = ({
	merchantCode,
	divisionCode,
	payoutRequestCode,
}: DeletePayoutRequestPayload): Promise<void> => {
	const url = IS_OPS
		? `main/api/v1/fund/payout-requests/merchants/${merchantCode}/divisions/${divisionCode}/${payoutRequestCode}`
		: `/v1/fund/payout-requests/${payoutRequestCode}`;

	const config = IS_OPS
		? {
				headers: {
					'Merchant-Code': merchantCode,
					'Merchant-Division-Code': divisionCode,
				},
		  }
		: {};

	return axios.delete(url, config);
};

/**
 * @function
 * @name getRegenerateBouncebackPayoutRequests
 * Triggers and Retrieves the number of regenerated bounceback payout requests from the server.
 *
 * @param {Object} payload - The payload object containing the parameters for the request.
 * @param {string} payload.merchantCode -- null if no merchant is selected
 * @param {string} payload.divisionCode -- null if no merchant is selected
 *
 * Regenerates -returned- payout requests for specific or all merchants
 * url for all merchants: 'main/api/v1/fund/trigger/regenerate-payout-requests'
 * url for specific merchant: 'main/api/v1/fund/merchants/{merchantCode}/merchantdivisions/{divisionCode}/trigger/regenerate-payout-requests'
 *
 * @returns {Promise<number>} A promise that resolves to the number of regenerated bounceback payout requests.
 *
 */
export const getRegenerateBouncebackPayoutRequests = ({
	merchantCode,
	divisionCode,
}: RegenerateBouncebackPayload): Promise<{ data: number }> => {
	let url = 'main/api/v1/fund/trigger/regenerate-payout-requests';
	if (merchantCode && divisionCode)
		url = `main/api/v1/fund/merchants/${merchantCode}/merchantdivisions/${divisionCode}/trigger/regenerate-payout-requests`;

	return axios.get(url);
};

/**
 * @function
 * @name getXmlFile
 * get a Base64 encoded data containing the XML file
 *
 * @param {string} merchantCode - The code of the merchant.
 * @param {string} divisionCode - The code of the division.
 * @param {string} xmlFileCode - The code of the xml file.
 *
 * @returns {Promise<{data: GetXmlFileResponse}>} A promise that resolves in an object with base64 encoded data.
 *
 */
export const getXmlFile = (
	merchantCode: string,
	divisionCode: string,
	xmlFileCode: string
): Promise<{ data: GetXmlFileResponse }> => {
	return axios.get(
		`/main/api/v1/reconciliation/merchants/${merchantCode}/merchantdivisions/${divisionCode}/files/${xmlFileCode}`
	);
};

/**
 * @function
 * @name postProcessPayoutRequests
 * Processes selected payout requests
 *
 *
 * url (no merchant or division information needed): 'main/api/v1/fund/payout-requests/process'
 *
 * @returns {Promise<{status: number}>} A promise with HTTP status
 *
 * @param payoutRequestCodes
 */
export const postProcessPayoutRequests = (
	payoutRequestCodes: string[]
): Promise<{ status: number }> => {
	const url = 'main/api/v1/fund/payout-requests/process';

	return axios.post(url, payoutRequestCodes);
};

/**
 * @function
 * @name postPayoutRequest
 * Create payout request
 *
 * @param {Object} payload - A list of payout request codes to be processed
 * @param {Object} payload.params - The params needed to trigger request
 * @param {String} payload.merchantCode - merchant code required
 * @param {String} payload.divisionCode - division code required
 *
 *
 * private url: 'main/api/v1/fund/payout-requests/merchants/{merchantCode}/divisions/{divisionCode}'
 * public url: 'v1/fund/payout-requests'
 *
 * @returns {Promise<{status: number}>} A promise with HTTP status and no body.
 *
 */
export const postPayoutRequest = ({
	merchantCode = null,
	divisionCode = null,
	params,
}: CreatePayoutRequestPayload): Promise<PayoutRequestResponse> => {
	const url = IS_OPS
		? `main/api/v1/fund/payout-requests/merchants/${merchantCode}/divisions/${divisionCode}/`
		: 'v1/fund/payout-requests';

	return axios.post(url, params);
};

/**
 * @function
 * @name getSplitData
 * get the split information for a transaction
 *
 * @param {String} code - transaction code required
 *
 * for transaction type PAYMENTS:
 * private url: '/main/api/v1/fund/split-payments/${code}'
 * public url: 'v1/fund/split-payments/${code}'
 *
 * for transaction type REFUNDS:
 * private url: '/main/api/v1/fund/split-refunds/${code}'
 * public url: 'v1/fund/split-refunds/${code}'
 *
 * @returns {Promise<{data: string}>} A promise that resolves into an object with json data.
 *
 */
export const getSplitData = (code: string, transactionType: string): Promise<GetSplitResponse> => {
	let url = '';
	if (transactionType === 'PAYMENT') {
		url = IS_OPS
			? `/main/api/v1/fund/split-payments/${code}`
			: `v1/fund/split-payments/${code}`;
	} else {
		// transactionType is 'REFUND'
		url = IS_OPS ? `/main/api/v1/fund/split-refunds/${code}` : `v1/fund/split-refunds/${code}`;
	}
	return axios.get(url);
};

/**
 * @function
 * @name postDivisionPayoutRequest
 * Create payout request
 *
 * @param {Object} payload - A list of payout request codes to be processed
 * @param {Object} payload.params - The params needed to trigger request
 * @param {String} payload.merchantCode - merchant code required
 * @param {String} payload.divisionCode - division code required
 *
 *
 * private url: 'main/api/v1/restricted/fund/merchants/{merchantCode}/divisions/{divisionCode}/own-payout-requests'
 * public url: 'v1/fund/payout-requests'
 *
 * @returns {Promise<PayoutRequestResponse>} A promise with HTTP status and no body.
 *
 */
export const postDivisionPayoutRequest = ({
	merchantCode,
	divisionCode,
	params,
}: CreatePayoutRequestPayload): Promise<PayoutRequestResponse> => {
	const url = IS_OPS
		? `main/api/v1/restricted/fund/merchants/${merchantCode}/divisions/${divisionCode}/own-payout-requests`
		: 'v1/fund/payout-requests';

	const headers = IS_OPS
		? {
				'Merchant-Code': merchantCode,
				'Merchant-Division-Code': divisionCode,
				'Payaut-Populate-Code-Fields': 'generate',
		  }
		: {};

	return axios.post(url, params, {
		headers,
	});
};

/**
 * @function
 * @name postTriggerScheduledPayout
 * Triggers scheduled payout for virtual account
 *
 * @param {Object} payload - A list of payout request codes to be processed
 * @param {Object} payload.virtualAccount - The virtual account for which we trigger the payout scheduler
 * @param {String} payload.merchantCode - merchant code required
 * @param {String} payload.divisionCode - division code required
 *
 *
 * private url: 'main/api/v1/fund/scheduled-payout/trigger/merchant/{merchantCode}/division/{divisionCode}/virtualaccount/{account}'
 *
 * @returns {Promise<TriggerScheduledResponse>} A promise with HTTP status and no body.
 *
 */
export const postTriggerScheduledPayout = ({
	merchantCode,
	divisionCode,
	virtualAccountCode,
}: TriggerScheduledPayoutRequest): Promise<TriggerScheduledResponse> => {
	const url = IS_OPS
		? `main/api/v1/fund/scheduled-payout/trigger/merchant/${merchantCode}/division/${divisionCode}/virtualaccount/${virtualAccountCode}`
		: `/v2/scheduled-payout/trigger/virtualaccount/${virtualAccountCode}`;

	return axios.post(url);
};

/**
 * @function
 * @name getSellerPayoutConfig
 * Gets a seller's payout configuration
 *
 * @param merchantCode
 * @param divisionCode
 * @param virtualAccountCode - the virtual account code of the seller
 *
 * @returns {Promise<PayoutConfig>} A promise with the payout configuration
 */
export const getSellerPayoutConfig = (
	merchantCode: string,
	divisionCode: string,
	virtualAccountCode: string
): Promise<PayoutConfig> => {
	const url = IS_OPS
		? `main/api/v2/fund/payout-config/account/${virtualAccountCode}`
		: `/v2/fund/payout-config/account/${virtualAccountCode}`;

	const headers: AxiosRequestConfig['headers'] = {
		'Merchant-Code': merchantCode,
		'Merchant-Division-Code': divisionCode,
	};

	return axios.get(url, { headers });
};

/**
 * @function
 * @name postProcessPayout
 * Processes selected payout batch
 *
 * @param {string} merchantCode - The code of the merchant.
 * @param {string} divisionCode - The code of the division.
 * @param {string} payoutCode - The code of the payout.
 * @returns {Promise<{data: MerchantPayout}>} A promise that resolves to the result of the post-processing.
 */
export const postProcessPayout = async (
	merchantCode: string,
	divisionCode: string,
	payoutCode: string
): Promise<{ data: MerchantPayout }> => {
	const headers: AxiosRequestConfig['headers'] = {
		'Merchant-Code': merchantCode,
		'Merchant-Division-Code': divisionCode,
	};

	return axios.post(
		`main/api/v1/fund/payouts/merchants/${merchantCode}/divisions/${divisionCode}/${payoutCode}`,
		{
			headers,
		}
	);
};

/**
 * @function
 * @name postGenerateReport
 * Requests the generation of a report
 *
 * @param {string} reportType - The report type to be generated
 * @param {GenerateReportParameters} params - Required date and account information to generate the report
 *
 * OPS url: `invoicing/merchants/${merchantCode}/divisions/${divisionCode}/reports/${reportType}`
 * MD url: 'v2/reports/{reportType}'
 *
 * @returns {Promise<{status: number}>} A promise with HTTP status and no body.
 */
export const postGenerateReport = async (
	merchantCode: string,
	divisionCode: string,
	reportType: string,
	params: GenerateReportParameters
): Promise<{ status: number }> => {
	const url = IS_OPS
		? `invoicing/merchants/${merchantCode}/divisions/${divisionCode}/reports/${reportType}`
		: `/v2/reports/${reportType}`;
	return axios.post(url, params);
};

/**
 * @function
 * @name getReports
 * Retrieves all the reports of a merchant given a specific report type
 *
 * @param {string} reportType
 * @param {GetReportsParameters} params
 *
 * url OPS: 'invoicing/v2/merchants/${merchantCode}/divisions/${divisionCode}/reports/${reportType}'
 * url MD: 'v2/reports/{reportType}'
 *
 * @returns {Promise<{ data: GetReportsResponse }>} A promise with the list of reports and pagination data
 */
export const getReports = async (
	merchantCode: string,
	divisionCode: string,
	reportType: keyof typeof REPORT_TYPES,
	params: GetReportsParameters
): Promise<{ data: GetReportsResponse }> => {
	const url = IS_OPS
		? `invoicing/merchants/${merchantCode}/divisions/${divisionCode}/reports/${reportType}`
		: `v2/reports/${reportType}`;
	return axios.get(url, { params });
};

/**
 * @function
 * @name triggerKyc
 * Triggers external provider kyc (currently Fourthline) for the provided accountOwners
 *
 * @param sellers the accountOwnerCodes
 *
 * url: '/kyc2/provider-request'
 *
 * @returns {Promise<{ status: number}>} A promise with the HTTP status code
 */
export const triggerKyc = async (sellers: string[]): Promise<{ status: number }> => {
	const url = 'kyc2/kyc2/provider-request';

	const triggerKycRequest = {
		accountHolderCodes: sellers,
	};

	return axios.post(url, triggerKycRequest);
};

/**
 * @function
 * @name getMerchantsWithPayoutRequests
 * Retrieves all merchants and their divisions including the
 * payout requests for each division.
 *
 * url: 'accountowner/api/v2/accountowners/merchants/payout-requests'
 *
 * @returns {Promise<{ data: GetMerchantsResponse }>} A promise with lists of accounts grouped by type
 */
export const getMerchantsWithPayoutRequests = async (
	params: GetMerchantsWithPayoutRequestsQueries
): Promise<{ data: GetMerchantsWithPayoutRequestsResponse }> => {
	// TODO: restore 1 based when pagination is fixed in the server
	// the page offsets + 1 and - 1 are needed right now because the sellers endpoint is 0 based in the server.
	// This should be fixed in the server some day. Then we remove the offsets here as well.
	const offsetParams = {
		...params,
		page: params.page && params.page > 0 ? params.page - 1 : 0,
	};

	const url = 'accountowner/api/v2/accountowners/merchants/payout-requests';

	const response = await axios.get(url, { params: offsetParams });

	return response;
};

/**
 * @function
 * @name postProcessDivisionPayoutRequests
 * Processes all seller's payout requests for a division
 *
 * url: 'main/api/v1/fund/payout-requests/merchants/{params.merchantCode}/divisions/{params.divisionCode}/process'
 */
export const postProcessDivisionPayoutRequests = async (
	params: ProcessDivisionPayoutRequestParams
) => {
	const url = `main/api/v1/fund/payout-requests/merchants/${params.merchantCode}/divisions/${params.divisionCode}/process`;

	return axios.post(url);
};

/**
 * @function
 * @name getMerchants
 * Get a list of merchants and their divisions
 *
 * OPS only
 * url: '/accountowner/api/v2/accountowners/merchants'
 *
 * @returns {Promise<{ data: GetMerchantsResponse }>}
 */
export const getMerchants = async (
	params?: GetMerchantsQueries
): Promise<{ data: GetMerchantsResponse }> => {
	const url = `/accountowner/api/v2/accountowners/merchants`;

	return axios.get(url, { params });
};

/**
 * @function
 * @name getMerchant
 * Get specific merchant by merchantCode and its divisions
 *
 * OPS only
 * url '/accountowner/api/v2/accountowners/merchants/{merchantCode}
 *
 * @returns {Promise<Merchant>}
 */
export const getMerchant = async (merchantCode: string): Promise<Merchant> => {
	const { data } = await axios.get(
		`/accountowner/api/v2/accountowners/merchants/${merchantCode}`
	);
	return data;
};

/**
 * @function
 * @name getSellers
 * Get all sellers for with accounts and balances
 *
 * url OPS: 'accountowner/api/v2/accountowners/merchants/{merchantCode}/divisions/{divisionCode}/accountholders'
 * url MD: '/v1/accountholders'
 *
 * @return {Promise<{ data: SellerResponse }>}
 */
export const getSellers = async ({
	merchantCode,
	divisionCode,
	params,
	signal,
}: {
	merchantCode?: string;
	divisionCode?: string;
	params: SellersRequestPayload;
	signal?: AbortSignal;
}): Promise<{ data: SellerResponse }> => {
	// TODO: restore 1 based when pagination is fixed in the server
	// the page offsets + 1 and - 1 are needed right now because the sellers endpoint is 0 based in the server.
	// This should be fixed in the server some day. Then we remove the offsets here as well.
	const offsetParams = {
		...params,
		page: params.page && params.page > 0 ? params.page - 1 : 0,
		_fragments: 'balances',
	};

	const url = IS_OPS
		? `accountowner/api/v2/accountowners/merchants/${merchantCode}/divisions/${divisionCode}/accountholders`
		: '/v1/accountholders';

	const { data } = await axios.get(url, { params: offsetParams, signal });
	data.pageNumber += 1;

	return { data };
};

/**
 * @function
 * @name getSellerDetails
 * Get detailed info about a seller using fragments
 *
 * @param {string} merchantCode - Required for the url of the request.
 * @param {string} divisionCode - Required for the url of the request.
 * @param {string} accountHolderCode - Required for the url of the request.
 * @param {string} params - Required for the url of the request.
 *
 * url OPS: 'accountowner/api/v2/accountowners/merchants/{merchantCode}/divisions/{divisionCode}/accountholders/{accountHolderCode}'
 * url MD: '/v1/accountholders/{accountHolderCode}'
 *
 * @return {Promise<{ data: SellerDetailsItem }>}
 */
export const getSellerDetails = async ({
	merchantCode,
	divisionCode,
	accountHolderCode,
	params,
}: SellerDetailsRequestPayload): Promise<{ data: SellerDetailsItem }> => {
	params = { ...params };

	const url = IS_OPS
		? // eslint-disable-next-line max-len
		  `accountowner/api/v2/accountowners/merchants/${merchantCode}/divisions/${divisionCode}/accountholders/${accountHolderCode}`
		: `v1/accountholders/${accountHolderCode}`;

	return axios.get(url, { params });
};

/**
 * @function
 * @name getKycTasks
 * Get detailed info about a seller using fragments
 *
 * url OPS: '/tasks?assignee={email}'
 *
 * @return {Promise<ApiResponse>}
 */
export const getKycTasks = async (email: string, params: QueryParameters): Promise<ApiResponse> => {
	const page = params.page ? params.page - 1 : 0;
	params = { ...params, page };
	const url = `kyc2/tasks?assignee=${email}`;

	const response = await axios.get(url, { params });

	response.data.pagination.page += 1;
	return response;
};

/**
 * @function
 * @name requestTaskForUser
 * Get detailed info about a seller using fragments
 *
 * url OPS: 'kyc2/tasks?assignee={user}'
 *
 * @return {Promise<ApiResponse>}
 */
export const requestTaskForUser = async (assignee: string): Promise<ApiResponse> => {
	const url = 'kyc2/tasks/assign';

	return axios.patch(url, { assignee });
};

/**
 * @function
 * @name assignUserToKycTask
 * Get detailed info about a seller using fragments
 *
 * url OPS: '/kyc2/tasks/${accountOwnerCode}/assign'
 *
 * @return {Promise<ApiResponse>}
 */
export const assignUserToKycTask = async (
	accountOwnerCode: string,
	assignee: string
): Promise<ApiResponse> => {
	const url = `kyc2/tasks/${accountOwnerCode}/assign`;

	return axios.patch(url, { assignee });
};

/**
 * @function
 * @name postSellersPendingKyc
 * Get detailed info about a seller using fragments
 *
 * url OPS: 'accountowner/api/v2/accountowners/accountholders'
 *
 * @return {Promise<{ data: SellerPendingKyc[] }>}
 */
export const postSellersPendingKyc = async (
	accountOwnerCodes: string[]
): Promise<{ data: SellerPendingKyc[] }> => {
	const url = 'accountowner/api/v2/accountowners/accountholders';

	return axios.post(url, accountOwnerCodes);
};

/**
 * @function
 * @name getPayoutRequestDetails
 * Get transaction list included in a payout request
 *
 * url OPS: 'insights/api/v2/merchants/{merchantCode}/divisions/{divisionCode}/payouts/{payoutRequestCode}/details'
 * url MD: 'v2/payout-requests/{payoutRequestCode}/details'
 *
 * @return {Promise<{ data: PayoutRequest }>}
 */
export const getPayoutRequestDetails = async ({
	merchantCode,
	divisionCode,
	payoutRequestCode,
}: PayoutRequestDetailsPayload): Promise<{ data: PayoutRequest }> => {
	const url = IS_OPS
		? `insights/api/v2/merchants/${merchantCode}/divisions/${divisionCode}/payout-requests/${payoutRequestCode}/details`
		: `v2/payout-requests/${payoutRequestCode}/details`;

	const params = {
		page: 0,
		limit: 25,
	};
	return axios.post(url, params);
};

/**
 * @function
 * @name getPayoutRequestOverview
 * Get overview info about a payout request
 *
 * @param {string} merchantCode - Required for the url of the request.
 * @param {string} divisionCode - Required for the url of the request.
 * @param {string} payoutRequestID - the virtual account code of the seller
 *
 * url OPS: 'insights/api/v2/merchants/{merchantCode}/divisions/{divisionCode}/payout-requests/{payoutRequestCode}/overview'
 * url MD: 'v2/payout-requests/{payoutRequestCode}/overview'
 *
 * @return {Promise<{ data: PayoutRequest }>}
 */
export const getPayoutRequestOverview = async ({
	merchantCode,
	divisionCode,
	payoutRequestCode,
}: PayoutRequestDetailsPayload): Promise<{ data: PayoutRequest }> => {
	const url = IS_OPS
		? `insights/api/v2/merchants/${merchantCode}/divisions/${divisionCode}/payout-requests/${payoutRequestCode}/overview`
		: `v2/payout-requests/${payoutRequestCode}/overview`;

	const config = IS_OPS
		? {
				headers: {
					'Merchant-Code': merchantCode,
					'Merchant-Division-Code': divisionCode,
				},
		  }
		: {};
	return axios.get(url, config);
};

/**
 * @function
 * @name getCurrencies
 * Get the currencies of a virtual account
 *
 * @param {string} virtualAccountCode - The code of the virtual account
 * @param {string} merchantCode
 * @param {string} divisionCode
 *
 * url OPS: '/main/api/v2/fund/internal/balance/{virtualAccountCode}'
 * url MD: '/v1/fund/balance/{virtualAccountCode}'
 *
 * @return {Promise<{ data: GetCurrenciesResponse }>}
 */
export const getCurrencies = async ({
	virtualAccountCode,
	merchantCode,
	divisionCode,
}: GetCurrenciesPayload): Promise<{ data: GetCurrenciesResponse }> => {
	const url = IS_OPS
		? `/main/api/v2/fund/internal/balance/${virtualAccountCode}`
		: `/v1/fund/balance/${virtualAccountCode}`;

	const config = IS_OPS
		? {
				headers: {
					'Merchant-Code': merchantCode,
					'Merchant-Division-Code': divisionCode,
				},
		  }
		: {};

	return axios.get(url, config);
};

/**
 * @function
 * @name getKYCHistoryOps
 * Get a list of all KYC changes for an account holder
 *
 * url OPS: '/kyc2/kyc2/merchants/${merchantCode}/divisions/${divisionCode}/accountholders/${accountHolderCode}/kyc-history'
 * url MD: '/v2/kyc/accountholders/{accountHolderCode}/kyc-history'
 * @return {Promise<{ data: KycHistory }>}
 */
export const getKYCHistory = async ({
	merchantCode,
	divisionCode,
	accountHolderCode,
}: GetKycHistoryPayload): Promise<{ data: KycHistoryItem[] }> => {
	const url = IS_OPS
		? `/kyc2/kyc2/merchants/${merchantCode}/divisions/${divisionCode}/accountholders/${accountHolderCode}/kyc-history`
		: `/v2/kyc/accountholders/${accountHolderCode}/kyc-history`;
	return axios.get(url);
};

/**
 * @function
 * @name getPspRawData
 * Fetches raw PSP data for a merchant
 *
 * url OPS: '/psp-adapter/merchants/{merchantCode}/divisions/{divisionCode}/files/{executionId}/download'
 *
 * @return {Promise<{ data: PspRawDataResponse }>}
 */
export const getPspRawData = async (
	{ merchantCode, divisionCode, executionId }: PspRawDataPayload,
	pagination: Pagination
): Promise<{ data: PspRawDataResponse }> => {
	const url = `/psp-adapter/merchants/${merchantCode}/divisions/${divisionCode}/files/${executionId}/download`;
	return axios.get(url, { params: pagination });
};

/**
 * @function
 * @name getSplitDetails
 * Fetches split details (amounts and types)
 * @return {Promise<{ data: SplitDetailsResponse }}
 */
export const getSplitDetails = async ({
	merchantCode,
	divisionCode,
	splitCode,
}: GetSplitDetailsPayload): Promise<{
	data: SplitDetailsResponse;
}> => {
	const url = IS_OPS
		? `/insights/api/v2/merchants/${merchantCode}/divisions/${divisionCode}/split-details/${splitCode}`
		: `/v2/split-details/${splitCode}`;

	return axios.get(url);
};

/**
 * @function
 * @name patchLegalForm
 * Changes legalForm for list of accountHolder codes
 * @return Promise<string>
 */
export const patchLegalForm = async (accountHolderCodes: Array<string>): Promise<string> => {
	const url = 'v2/accountholders/change-legal-form';

	return axios.patch(url, { accountHolderCodes });
};

/**
 * @function
 * @name deleteAccountHolder
 * Delete accountHolder from code
 * @return {Promise<{ data: AccountHolder }}
 */
export const deleteAccountHolder = async ({
	merchantCode,
	divisionCode,
	accountHolderCode,
}: deleteAccountHolderPayload): Promise<{
	data: SplitDetailsResponse;
}> => {
	const url = IS_OPS
		? `/accountowner/api/v2/accountowners/merchants/${merchantCode}/divisions/${divisionCode}/accountholders/${accountHolderCode}`
		: `/v1/accountholders/${accountHolderCode}`;

	return axios.delete(url);
};
